import { use, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { Card, ConfirmationDialog, EmptyState } from '~components';
import FormDialog from '~components/dialogs/formDialog';
import VectorImage from '~components/media/vectorImage';
import { generalInformation } from '~constants';
import { StatusVariant, UserRoleEnum } from '~enums';
import AddOfflineKeyForm from '~features/offlineAccess/forms/addOfflineKeyForm';
import OfflineAccessDataGrid from '~features/offlineAccess/offlineAccessDataGrid';
import OfflineAccessService from '~features/offlineAccess/services/offlineAccessService';
import useAuthorize from '~hooks/useAuthorize';
import { extendDevice } from '~interfaces/device';
import { getService } from '~services/serviceFactory';

import DeviceActionsBar from '../components/deviceActionBar';
import DeviceContext from '../providers/deviceContext';

type DeviceOfflineAccessActions = 'addOfflineKey';
const offlineAccessService = getService(OfflineAccessService);

const DeviceOfflineAccessContainer = () => {
	const auth = useAuthorize();
	const { t } = useTranslation('entities');
	const { enqueueSnackbar } = useSnackbar();
	const isSuperAdmin = auth.authorize([UserRoleEnum.SuperAdmin]);
	const deviceContext = use(DeviceContext);
	const deviceId = deviceContext.device?.id ?? '0';
	const organisationId = deviceContext.overwrittenClientId;
	const [userAction, setUserAction] = useState<DeviceOfflineAccessActions | null>(null);
	const [isAddingOfflineKey, setIsAddingOfflineKey] = useState<boolean>(false);
	const [isRemovingOfflineKey, setIsRemovingOfflineKey] = useState<boolean>(false);
	const [offlineKeysRefreshTrigger, setOfflineKeysRefreshTrigger] = useState(0);
	const [offlineKeyIdToRemove, setOfflineKeyIdToRemove] = useState<string | null>(null);
	const showConnectivityProfileSettingButton =
		extendDevice(deviceContext.device)?.showConnectivityProfileSettingButton ?? false;

	const clearUserAction = () => {
		setUserAction(null);
		setOfflineKeyIdToRemove(null);
	};

	const addOfflineKey = (keyId: string) => {
		setIsAddingOfflineKey(true);
		offlineAccessService
			.postOfflineKey({ keyId, deviceId })
			.then((_) => {
				setOfflineKeysRefreshTrigger((current) => current + 1);
				clearUserAction();
				enqueueSnackbar(t('offlineKeyAddedSuccessfully', { ns: 'toast' }), {
					variant: 'success',
				});
			})
			.catch((_error) => {
				enqueueSnackbar(t('offlineKeyFailedToAdd', { ns: 'toast' }), {
					variant: 'error',
				});
			})
			.finally(() => {
				setIsAddingOfflineKey(false);
			});
	};

	const requestRemoveOfflineKey = (id: string) => {
		setOfflineKeyIdToRemove(id);
	};

	const confirmRemoveOfflineKey = () => {
		if (offlineKeyIdToRemove == null) {
			return;
		}

		setIsRemovingOfflineKey(true);
		offlineAccessService
			.deleteOfflineKey(offlineKeyIdToRemove)
			.then(() => {
				setOfflineKeysRefreshTrigger((current) => current + 1);
				clearUserAction();
				enqueueSnackbar(t('offlineKeyRemovedSuccessfully', { ns: 'toast' }), {
					variant: 'success',
				});
			})
			.catch((_error) => {
				enqueueSnackbar(t('offlineKeyFailedToRemove', { ns: 'toast' }), {
					variant: 'error',
				});
			})
			.finally(() => {
				setIsRemovingOfflineKey(false);
			});
	};

	return (
		<>
			<DeviceActionsBar
				deviceId={deviceId}
				organisationId={organisationId}
				showUnassignButton={
					isSuperAdmin &&
					organisationId != undefined &&
					organisationId != generalInformation.manufacturingOrganisationId
				}
				showAssignButton={
					isSuperAdmin && organisationId == generalInformation.manufacturingOrganisationId
				}
				showEditButton={isSuperAdmin}
				showChangeResourceGroupButton={
					isSuperAdmin && organisationId != generalInformation.manufacturingOrganisationId
				}
				showDeleteButton={isSuperAdmin}
				showConnectivityProfileSettingButton={showConnectivityProfileSettingButton}
				showRemoteActionsButton={isSuperAdmin}
			/>
			<Card
				title={
					t('offlineAccess', { ns: 'nav' }) + ' ' + t('device.labels.plural', { ns: 'entities' })
				}
				sx={{ height: '100%' }}
			>
				{deviceId != null && organisationId != null ?
					<OfflineAccessDataGrid
						source={'device'}
						deviceId={deviceId}
						overwrittenClientId={organisationId}
						refreshTrigger={offlineKeysRefreshTrigger}
						showAddOfflineKeyButton={true}
						onAddOfflineKeyButtonClicked={() => setUserAction('addOfflineKey')}
						enableRemoveOfflineKey={true}
						onRemoveOfflineKey={(id) => requestRemoveOfflineKey(id)}
					/>
				:	<EmptyState
						icon='icon/bar-chart-square'
						title={t('noDataTitle', { ns: 'table' })}
						description={t('noDataDescription', { ns: 'table' })}
					/>
				}
			</Card>
			<FormDialog
				open={userAction === 'addOfflineKey'}
				title={
					t('add', { ns: 'actions' }) +
					' ' +
					t('key.labels.single', { ns: 'entities' }).toLowerCase()
				}
				loading={isAddingOfflineKey}
			>
				<AddOfflineKeyForm
					organisationId={organisationId}
					onSubmit={async (formData) => addOfflineKey(formData.keyId)}
					onCancel={clearUserAction}
				/>
			</FormDialog>
			<ConfirmationDialog
				open={offlineKeyIdToRemove != null}
				title={t('key.labels.removeKeyDialogTitle', { ns: 'entities' })}
				helperText={t('key.labels.removeKeyDialogDescription', { ns: 'entities' })}
				confirmButtonText={t('remove', { ns: 'actions' })}
				variant={StatusVariant.Destructive}
				icon={<VectorImage name='icon/remove' />}
				onConfirm={confirmRemoveOfflineKey}
				onClose={clearUserAction}
				loading={isRemovingOfflineKey}
			/>
		</>
	);
};

export default DeviceOfflineAccessContainer;
